import React, { useEffect } from 'react';
import MetaTags from '../components/SEO/MetaTags';
import {
  UserGroupIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  HeartIcon,
  AcademicCapIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const About = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const team = [
    {
      name: 'Fahad K',
      role: 'Managing Director & Chartered Accountant',
      experience: '12+ Years',
      specialization: 'Tax Planning & Corporate Finance',
      qualification: 'FCA, DISA, MBA',
      image: '/assets/images/team/fahad.jpg',
    },
    {
      name: 'Shamna K',
      role: 'Senior Partner & Chartered Accountant',
      experience: '10+ Years',
      specialization: 'GST & Indirect Taxation',
      qualification: 'FCA, LLB',
      image: '/assets/images/team/shamna.jpg',
    },
    {
      name: 'Mohammed Faris',
      role: 'Partner & Chartered Accountant',
      experience: '8+ Years',
      specialization: 'Audit & Assurance',
      qualification: 'FCA, CISA',
      image: '/assets/images/team/faris.jpg',
    },
  ];

  const values = [
    {
      icon: ShieldCheckIcon,
      title: 'Integrity First',
      description: 'We uphold the highest ethical standards in every interaction, ensuring transparency and trust in all our financial dealings.',
    },
    {
      icon: LightBulbIcon,
      title: 'Innovation Driven',
      description: 'Embracing cutting-edge technology and modern approaches to deliver efficient, accurate, and timely financial solutions.',
    },
    {
      icon: UserGroupIcon,
      title: 'Client Centric',
      description: 'Every decision we make is focused on maximizing value for our clients with personalized attention and tailored solutions.',
    },
    {
      icon: HeartIcon,
      title: 'Community Focus',
      description: 'Deeply rooted in Kerala, we understand local business dynamics and contribute to regional economic growth.',
    },
  ];

  const milestones = [
    { year: '2012', event: 'Founded in Malappuram', description: 'Started with a vision to provide world-class financial services' },
    { year: '2015', event: 'Digital Transformation', description: 'Pioneered digital-first accounting practices in Kerala' },
    { year: '2018', event: '500+ Clients', description: 'Reached significant milestone serving diverse industries' },
    { year: '2021', event: 'NAQD Consulting Pvt Ltd', description: 'Incorporated as private limited company' },
    { year: '2023', event: 'Regional Expansion', description: 'Extended services across Kerala with multiple branches' },
  ];

  return (
    <div>
      <MetaTags
        title="About NAQD Consulting - Leading CA Firm in Malappuram, Kerala"
        description="Learn about NAQD Consulting Private Limited, Kerala's trusted chartered accountancy firm. Our story, team, values, and commitment to excellence in financial services."
        keywords="about naqd consulting, chartered accountant malappuram, ca firm kerala, tax consultant story, accounting services malappuram"
      />

      {/* Hero Section */}
      {/* Hero Section */}
      <section
        className="section-padding relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/assets/images/backgrounds/business-hero.png')" }}
      >
        <div className="absolute inset-0 bg-naqd-primary/80"></div>
        <div className="container-custom text-center relative z-10">
          <div className="animate-on-scroll">
            <h1 className="heading-primary mb-6 text-white">
              About <span className="text-naqd-green">NAQD Consulting</span>
            </h1>
            <p className="text-xl text-white/90 dark:text-gray-300 max-w-4xl mx-auto">
              From a small practice in Malappuram to becoming Kerala's most trusted
              chartered accountancy firm, our journey is built on integrity, innovation,
              and unwavering commitment to client success.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll">
              <h2 className="heading-secondary mb-6">
                Our <span className="text-gradient">Story</span>
              </h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  Founded in 2012 by a team of passionate chartered accountants, NAQD
                  Consulting began with a simple mission: to make quality financial
                  services accessible to businesses and individuals in Kerala.
                </p>
                <p>
                  What started as a small practice in Malappuram has grown into one
                  of Kerala's most respected chartered accountancy firms, serving over
                  500 clients across diverse industries.
                </p>
                <p>
                  Our name "NAQD" reflects our commitment to transparency and
                  immediacy in financial dealings – qualities that have become the
                  cornerstone of our practice.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-naqd-green">500+</div>
                  <div className="text-sm text-gray-400">Happy Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-naqd-green">10+</div>
                  <div className="text-sm text-gray-400">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-naqd-green">₹50Cr+</div>
                  <div className="text-sm text-gray-400">Tax Saved</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-naqd-green">95%</div>
                  <div className="text-sm text-gray-400">Client Retention</div>
                </div>
              </div>
            </div>

            <div className="animate-on-scroll">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
                <h3 className="text-2xl font-semibold mb-6">Our Journey</h3>
                <div className="space-y-6">
                  {milestones.map((milestone, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-naqd-green rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {milestone.year.slice(2)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-naqd-light">{milestone.event}</h4>
                        <p className="text-sm text-gray-400">{milestone.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-naqd-dark-gray/50">
        <div className="container-custom">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="heading-secondary">
              Our Core <span className="text-gradient">Values</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              These principles guide every decision we make and every service we provide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="card text-center group animate-on-scroll"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-naqd-green/10 rounded-full mb-4 group-hover:bg-naqd-green/20 transition-colors">
                  <value.icon className="w-8 h-8 text-naqd-green" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                <p className="text-gray-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="heading-secondary">
              Meet Our <span className="text-gradient">Expert Team</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Certified chartered accountants with deep expertise in Kerala taxation
              and compliance, committed to your financial success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="card text-center animate-on-scroll"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-32 h-32 bg-gradient-to-br from-naqd-green to-green-400 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <UserGroupIcon className="w-16 h-16 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
                <p className="text-naqd-green font-medium mb-2">{member.role}</p>
                <div className="space-y-1 text-sm text-gray-400">
                  <p><BriefcaseIcon className="w-4 h-4 inline mr-1" />{member.experience}</p>
                  <p><AcademicCapIcon className="w-4 h-4 inline mr-1" />{member.qualification}</p>
                </div>
                <p className="text-sm text-gray-300 mt-3">{member.specialization}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-naqd-dark-gray/50">
        <div className="container-custom">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="heading-secondary">
              Why Businesses Choose <span className="text-gradient">NAQD</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: 'Local Expertise',
                points: [
                  'Deep understanding of Kerala taxation landscape',
                  'Knowledge of regional business practices',
                  'Malayalam language support',
                  'Cultural sensitivity in financial advice',
                ],
              },
              {
                title: 'Digital Excellence',
                points: [
                  '100% paperless processes',
                  'Secure cloud-based document management',
                  'Real-time compliance tracking',
                  'Mobile-friendly client portal',
                ],
              },
              {
                title: 'Comprehensive Services',
                points: [
                  'One-stop solution for all financial needs',
                  'Cross-functional expertise',
                  'Seamless integration of services',
                  'Proactive compliance management',
                ],
              },
              {
                title: 'Client-First Approach',
                points: [
                  'Personal attention to every client',
                  'Transparent pricing with no hidden costs',
                  'Regular communication and updates',
                  'Long-term relationship building',
                ],
              },
            ].map((section, index) => (
              <div key={index} className="card animate-on-scroll">
                <h3 className="text-xl font-semibold mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.points.map((point, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <CheckCircleIcon className="w-5 h-5 text-naqd-green flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-300">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto animate-on-scroll">
            <h2 className="heading-secondary">
              Ready to Work with Kerala's Best?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Experience the NAQD difference with our expert team dedicated to your
              financial success. Let's build your prosperous future together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn-primary text-lg px-8 py-4">
                Schedule Consultation
              </a>
              <a href="tel:+919074286763" className="btn-secondary text-lg px-8 py-4">
                Call: +91 90742 86763
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;